// Video dispatcher — Kommunicate rich-message click handler.
//
// Why this module exists:
//   Our bot renders "video card" rich messages whose primary button has a
//   payload of shape:
//     {
//       action:       "open_fintepla_video",
//       pageUrl:      "https://www.fintepla.com/...family-stories",
//       domSelector:  "[data-video-slug=\"upp\"]",
//       deeplink:     "#video-upp",      // optional
//       videoId:      "946803926",       // optional, analytics only
//       videoTitle:   "The Upp family"   // optional, analytics only
//     }
//
//   When the user clicks the card, we want:
//     - If the user is already on the correct family-stories page,
//       dispatch a synthetic click on the tagged poster
//       (`data-video-slug` attribute is rendered server-side by the
//       Drupal CMS — see docs/fintepla-cms-changes.md) so the Drupal
//       modal opens in place.
//     - If they are on any other page, navigate to `pageUrl` (plus an
//       optional `deeplink` fragment) so the landing page can open the
//       modal itself.
//     - If we're on the right page but the selector can't find the
//       poster (stale selector, lazy block, template change), fall back
//       to setting `location.hash` to the deeplink so any hash-based
//       opener on the page still has a chance.
//
// Timing constraint:
//   The Kommunicate SDK reads `window.kommunicateSettings` at
//   `Kommunicate.init(...)` time. This module MUST run BEFORE the SDK
//   script is injected, so it's called from embed.js's mount() before
//   setupKommunicateUi(). kommunicate.js then merges whatever handlers
//   this module registered with its own onInit and forwards them to the
//   SDK via `window.kommunicate._globals`.
//
// Chaining policy:
//   We never stomp an existing handler. If something (a previous mount,
//   an external GTM tag, a QA override) already registered
//   `onRichMessageLinkClick` / `onMessageReceived`, we save the previous
//   reference and invoke it after our own logic runs, preserving its
//   return value where it matters.
//
// ES2018 only, zero external dependencies. Idempotent — repeat calls
// short-circuit on `window.__finteplaVideoDispatcherInitialized`.

const INIT_FLAG = '__finteplaVideoDispatcherInitialized'
const VIDEO_ACTION = 'open_fintepla_video'
const GTM_EVENT = 'video_card_shown'

// Build-time origin rewrite. The webhook always emits
// `https://www.fintepla.com/...` in `payload.pageUrl` — canonical
// production origin — regardless of which environment is actually
// serving the mount script. At build time we substitute
// `__SITE_ORIGIN__` with the host origin this bundle is going to run
// on (see the Vite replace plugin / Prompt H). When the two differ,
// `rewriteOrigin()` maps canonical URLs onto the current host so a
// staging tester clicking a video card does NOT get bounced out to
// production (breaking SSO-protected QA).
//
// Sentinel behavior: when the placeholder has NOT been substituted
// yet (running tests against the raw source, or serving the dev
// playground that never hits the replace step), we treat it as a
// production no-op. This keeps every pre-Prompt-F test green.
const SITE_ORIGIN = '__SITE_ORIGIN__'
const CANONICAL_ORIGIN = 'https://www.fintepla.com'

function rewriteOrigin(pageUrl) {
  if (SITE_ORIGIN === CANONICAL_ORIGIN) return pageUrl
  // Unsubstituted placeholder → behave as production.
  if (SITE_ORIGIN === '__SITE_ORIGIN__') return pageUrl
  try {
    const u = new URL(pageUrl, CANONICAL_ORIGIN)
    if (u.origin === CANONICAL_ORIGIN) {
      return SITE_ORIGIN + u.pathname + u.search + u.hash
    }
    return pageUrl
  } catch {
    return pageUrl
  }
}

// Exported strictly so the regression tests can assert the "production
// build is a pass-through" invariant directly (see Prompt G). Not
// part of the embed's public API — do not import from application
// code.
export { rewriteOrigin as __rewriteOrigin__ }

// Normalize `origin + pathname` with a trailing-slash-insensitive rule
// so `/family-stories` and `/family-stories/` compare equal.
function normalizeOriginPath(url) {
  let parsed
  try {
    parsed = new URL(url, window.location.href)
  } catch {
    return null
  }
  let pathname = parsed.pathname || '/'
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1)
  }
  return parsed.origin + pathname
}

function currentOriginPath() {
  return normalizeOriginPath(window.location.href)
}

function stripLeadingMarker(value) {
  // Strip a leading `?` or `#` so the caller can pass either form in
  // `payload.deeplink` without us double-stacking markers.
  return typeof value === 'string' ? value.replace(/^[?#]+/, '') : ''
}

function extractPayload(args) {
  // Kommunicate has shipped two shapes for `onRichMessageLinkClick`
  // across recent SDK versions:
  //   - (event, action, data)   → classic
  //   - (event, payload)        → newer rich-message callbacks
  // We accept either by scanning arguments for the first object that
  // looks like our payload (has `.action`).
  for (let i = 1; i < args.length; i += 1) {
    const candidate = args[i]
    if (candidate && typeof candidate === 'object' && 'action' in candidate) {
      return candidate
    }
  }
  // Fallback: some builds pass the payload as the last argument even if
  // it doesn't carry `.action` at the top level. Be permissive here —
  // our caller checks `.action` explicitly.
  return args[args.length - 1] || null
}

function handleRichMessageLinkClick(event, payload) {
  if (!payload || payload.action !== VIDEO_ACTION) return false
  if (!payload.pageUrl) return false

  // Build-time origin rewrite: if this bundle was built for staging,
  // remap canonical-production `pageUrl` values onto the staging host.
  // No-op in production and when the placeholder hasn't been resolved.
  const pageUrl = rewriteOrigin(payload.pageUrl)

  // Stop Kommunicate's default "open link in new tab" behavior the
  // moment we know this is our custom action. We do this before any
  // further checks so a malformed payload still won't navigate away.
  try {
    event?.preventDefault?.()
  } catch {
    // no-op: some SDK builds pass a plain object instead of a real Event
  }

  const targetOriginPath = normalizeOriginPath(pageUrl)
  const hereOriginPath = currentOriginPath()

  if (targetOriginPath && targetOriginPath === hereOriginPath) {
    // Same page — try the in-place click path first.
    if (payload.domSelector) {
      let poster
      try {
        poster = document.querySelector(payload.domSelector)
      } catch {
        // Malformed selector — treat as a miss and fall through to the
        // deeplink-hash fallback below.
      }
      if (poster) {
        poster.click()
        return true
      }
    }

    // Selector missing or stale. Hash fallback gives any page-level
    // hash-based opener (including a future Drupal behavior) a chance.
    const hash = stripLeadingMarker(payload.deeplink)
    if (hash) {
      window.location.hash = hash
    }
    return true
  }

  // Different page — hand off by navigating. Concatenate the deeplink
  // as-is because it may be a query string (`?video=upp`) or a hash
  // (`#video-upp`); the target page decides how to consume it.
  const suffix = typeof payload.deeplink === 'string' ? payload.deeplink : ''
  window.location.assign(pageUrl + suffix)
  return true
}

function collectVideoButtons(message) {
  // A rich message can expose actionable buttons in a few different
  // metadata shapes (templateId 6 / 10 / custom). We walk the common
  // containers and collect anything that looks like our payload.
  const out = []
  if (!message || typeof message !== 'object') return out

  const metadata = message.metadata || message.payload || {}

  // 1. `metadata.payload` is a JSON string of an array of buttons in
  //    several Kommunicate rich templates.
  if (typeof metadata.payload === 'string') {
    try {
      const parsed = JSON.parse(metadata.payload)
      if (Array.isArray(parsed)) {
        for (const entry of parsed) {
          const action = entry?.action?.payload || entry?.action || entry
          if (action && action.action === VIDEO_ACTION) out.push(action)
        }
      } else if (parsed && parsed.action === VIDEO_ACTION) {
        out.push(parsed)
      }
    } catch {
      // non-JSON string metadata; ignore
    }
  }

  // 2. Direct buttons array on metadata.
  const buttons = Array.isArray(metadata.buttons) ? metadata.buttons : []
  for (const btn of buttons) {
    const action = btn?.action?.payload || btn?.action || btn
    if (action && action.action === VIDEO_ACTION) out.push(action)
  }

  // 3. Top-level metadata match (single-card messages).
  if (metadata.action === VIDEO_ACTION) out.push(metadata)

  return out
}

function pushVideoCardShown(payload) {
  if (!Array.isArray(window.dataLayer)) return
  window.dataLayer.push({
    event: GTM_EVENT,
    videoId: payload.videoId || null,
    videoTitle: payload.videoTitle || null,
    pageUrl: payload.pageUrl || null,
    // Build-time environment tag so GTM / GA can segment staging QA
    // traffic from production. Derived from the same SITE_ORIGIN
    // substitution used by rewriteOrigin() — zero runtime sniffing.
    environment:
      SITE_ORIGIN === CANONICAL_ORIGIN ? 'production' : 'staging',
  })
}

function handleMessageReceived(response) {
  if (!response) return
  // Kommunicate's `onMessageReceived` has passed both a single message
  // object and an array/wrapper across versions. Normalize to a flat
  // list of message-like objects.
  let messages
  if (Array.isArray(response)) {
    messages = response
  } else if (Array.isArray(response.messages)) {
    messages = response.messages
  } else if (Array.isArray(response.message)) {
    messages = response.message
  } else {
    messages = [response]
  }

  for (const msg of messages) {
    const videoPayloads = collectVideoButtons(msg)
    for (const payload of videoPayloads) {
      pushVideoCardShown(payload)
    }
  }
}

// Public entry point. Registers (and chains) the two Kommunicate
// settings hooks on `window.kommunicateSettings`, creating the object
// if necessary. MUST be called before `Kommunicate.init(...)`.
export function finteplaVideoDispatcher() {
  if (typeof window === 'undefined') return
  if (window[INIT_FLAG]) return
  window[INIT_FLAG] = true

  const settings = (window.kommunicateSettings = window.kommunicateSettings || {})

  // --- onRichMessageLinkClick --------------------------------------------
  const previousLinkClick =
    typeof settings.onRichMessageLinkClick === 'function'
      ? settings.onRichMessageLinkClick
      : null

  settings.onRichMessageLinkClick = function (...args) {
    const event = args[0]
    const payload = extractPayload(args)
    let handled = false
    try {
      handled = handleRichMessageLinkClick(event, payload)
    } catch (err) {
      // Never let our handler break Kommunicate's own flow.
      console.warn('[Fintepla] video dispatcher error', err)
    }

    // Chain. Previous handler runs even when we handled the click so
    // downstream analytics / custom hooks still see every event, unless
    // the event has been fully prevented — in which case most chained
    // handlers will no-op anyway.
    if (previousLinkClick) {
      try {
        return previousLinkClick.apply(this, args)
      } catch (err) {
        console.warn('[Fintepla] chained onRichMessageLinkClick threw', err)
      }
    }
    return handled
  }

  // --- onMessageReceived (analytics) -------------------------------------
  const previousMessageReceived =
    typeof settings.onMessageReceived === 'function'
      ? settings.onMessageReceived
      : null

  settings.onMessageReceived = function (response) {
    try {
      handleMessageReceived(response)
    } catch (err) {
      console.warn('[Fintepla] onMessageReceived analytics error', err)
    }

    if (previousMessageReceived) {
      try {
        return previousMessageReceived.call(this, response)
      } catch (err) {
        console.warn('[Fintepla] chained onMessageReceived threw', err)
      }
    }
    return undefined
  }
}

// Exposed for console-driven QA ("does the payload parse?") and for the
// SDK bootstrap in kommunicate.js to forward these handlers into the
// final settings object it hands to Kommunicate.init.
if (typeof window !== 'undefined') {
  window.finteplaVideoDispatcher = finteplaVideoDispatcher
}

